import type {
  CommandPlannerHostStatus,
  ModelOfferingRef,
  NodeOffering,
  NodePresence,
  RuntimeOutage,
  RuntimeProof,
} from './data';
import { connectionState, type OperationalStatus } from './presence';

export interface PlannerHostOption {
  readonly key: string;
  readonly nodeId: string;
  readonly nodeName: string;
  readonly runtimeKey: string;
  readonly runtimeLabel: string;
  readonly connected: boolean;
  readonly available: boolean;
  readonly unavailableReason: string | null;
  readonly preferred: boolean;
}

export interface PlannerHostDraft {
  readonly choice: string;
  readonly fallback: 'none' | 'api';
  readonly dirty: boolean;
}

function runtimeLabel(
  nodeId: string,
  runtimeKey: string,
  proofs: readonly RuntimeProof[],
  offerings: readonly ModelOfferingRef[],
): string {
  const proof = proofs.find((row) => row.node_id === nodeId && row.runtime_key === runtimeKey);
  if (proof?.runtime_label?.trim()) return proof.runtime_label.trim();
  const offering = offerings.find((row) => row.runtime_key === runtimeKey);
  return offering?.provider_label.trim() || runtimeKey;
}

function activeOutage(
  nodeId: string,
  runtimeKey: string,
  outages: readonly RuntimeOutage[],
): RuntimeOutage | null {
  return outages.find((row) =>
    row.node_id === nodeId
    && row.runtime_key === runtimeKey
    && !row.expired
  ) ?? null;
}

export function plannerHostOptions(input: {
  readonly nodes: readonly NodePresence[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
  readonly runtimeProofs: readonly RuntimeProof[];
  readonly outages: readonly RuntimeOutage[];
  readonly preferredRuntimeKey: string | null;
  readonly nowMs: number;
}): readonly PlannerHostOption[] {
  const modelByOffering = new Map(input.modelOfferings.map((row) => [row.key, row]));
  const nodeById = new Map(input.nodes.map((node) => [node.node_id, node]));
  const seen = new Set<string>();
  const rows: PlannerHostOption[] = [];

  for (const reported of input.nodeOfferings) {
    const offering = modelByOffering.get(reported.offering_key);
    const node = nodeById.get(reported.node_id);
    if (!offering || !node) continue;

    const key = `${node.node_id}::${offering.runtime_key}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const outage = activeOutage(node.node_id, offering.runtime_key, input.outages);
    rows.push({
      key,
      nodeId: node.node_id,
      nodeName: node.name,
      runtimeKey: offering.runtime_key,
      runtimeLabel: runtimeLabel(node.node_id, offering.runtime_key, input.runtimeProofs, input.modelOfferings),
      connected: connectionState({
        lastSeenAt: node.last_seen_at,
        status: node.status as OperationalStatus,
        nowMs: input.nowMs,
      }) === 'connected',
      available: outage === null,
      unavailableReason: outage?.reason_code ?? null,
      preferred: offering.runtime_key === input.preferredRuntimeKey,
    });
  }

  return rows.sort((left, right) =>
    Number(right.preferred) - Number(left.preferred)
    || Number(right.connected && right.available) - Number(left.connected && left.available)
    || left.nodeName.localeCompare(right.nodeName)
    || left.runtimeLabel.localeCompare(right.runtimeLabel),
  );
}

export function plannerHostConfiguredKey(status: CommandPlannerHostStatus | null): string {
  return status === null ? '' : `${status.node_id}::${status.runtime_key}`;
}

export function cleanPlannerHostDraft(
  status: CommandPlannerHostStatus | null,
  options: readonly PlannerHostOption[],
): PlannerHostDraft {
  const configured = plannerHostConfiguredKey(status);
  const firstSelectable = options.find((option) => option.connected && option.available)?.key ?? '';
  return {
    choice: configured || firstSelectable,
    fallback: status?.fallback_mode ?? 'none',
    dirty: false,
  };
}

/**
 * Presence/runtime refreshes may reorder or disable options, but they must never
 * overwrite an owner's unsaved selection. A clean form follows authoritative
 * server state immediately.
 */
export function reconcilePlannerHostDraft(
  current: PlannerHostDraft,
  status: CommandPlannerHostStatus | null,
  options: readonly PlannerHostOption[],
): PlannerHostDraft {
  return current.dirty ? current : cleanPlannerHostDraft(status, options);
}

export function plannerHostOptionLabel(option: PlannerHostOption): string {
  const parts = [option.nodeName, option.runtimeLabel];
  if (option.preferred) parts.push('preferred');
  if (!option.connected) parts.push('offline');
  else if (!option.available) {
    const reason = option.unavailableReason?.replaceAll('_', ' ') ?? 'unavailable';
    parts.push(`unavailable: ${reason}`);
  }
  return parts.join(' · ');
}
